import React, { useRef } from "react";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { LOGO, fmtDate } from "@/lib/helpers";
import { Download, Printer } from "lucide-react";

export const WelcomeCertificate = ({ employee, showActions = true }) => {
  const ref = useRef(null);
  const designation = employee.designation || "Data Entry Operator";
  const issued = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  const capture = async () => toPng(ref.current, { pixelRatio: 3, cacheBust: true, skipFonts: true, backgroundColor: "#ffffff" });
  const download = async () => {
    const url = await capture();
    const a = document.createElement("a");
    a.href = url; a.download = `VNS_Welcome_${(employee.name || "").replace(/\s+/g, "_")}.png`; a.click();
  };
  const print = async () => {
    const url = await capture();
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Welcome · ${employee.name}</title><style>@page{size:landscape;margin:8mm}body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh}img{max-width:100%}</style></head><body><img src="${url}" onload="window.focus();window.print();window.close();"/></body></html>`);
    w.document.close();
  };

  return (
    <div className="inline-block" data-testid={`certificate-${employee.employee_id}`}>
      <div ref={ref} className="relative w-[760px] h-[537px] bg-white p-3">
        {/* decorative frame */}
        <div className="absolute inset-3 border-[3px] border-gold rounded-sm" />
        <div className="absolute inset-[18px] border border-navy/30 rounded-sm" />
        {/* corner flourishes */}
        <div className="absolute left-3 top-3 h-10 w-10 border-l-4 border-t-4 border-navy rounded-tl-sm" />
        <div className="absolute right-3 top-3 h-10 w-10 border-r-4 border-t-4 border-navy rounded-tr-sm" />
        <div className="absolute left-3 bottom-3 h-10 w-10 border-l-4 border-b-4 border-navy rounded-bl-sm" />
        <div className="absolute right-3 bottom-3 h-10 w-10 border-r-4 border-b-4 border-navy rounded-br-sm" />
        {/* watermark */}
        <img src={LOGO} alt="" className="pointer-events-none absolute left-1/2 top-1/2 w-[300px] -translate-x-1/2 -translate-y-1/2 opacity-[0.05]" />

        <div className="relative flex h-full flex-col items-center px-16 py-10 text-center">
          <img src={LOGO} alt="VNS" className="h-16 w-16 object-contain" />
          <h2 className="mt-2 font-heading text-lg font-extrabold tracking-tight text-navy">VNS VINAYAKA GROUP OF ENTERPRISES</h2>
          <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-gold-dark">Data Entry Division</p>

          <h1 className="mt-5 font-heading text-4xl font-extrabold uppercase tracking-wide text-navy">Welcome Aboard</h1>
          <div className="mx-auto mt-1 h-[3px] w-28 rounded bg-gold" />

          <p className="mt-6 text-sm text-slate-500">This certificate is proudly presented to</p>
          <p className="mt-1 font-heading text-3xl font-extrabold text-gold-dark">{employee.name}</p>

          <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600">
            on joining <span className="font-semibold text-navy">VNS Vinayaka Group of Enterprises</span> as{" "}
            <span className="font-semibold text-navy">{designation}</span>. We are delighted to have you as part of our
            family and wish you a rewarding and successful journey with us.
          </p>

          <div className="mt-5 flex items-center gap-8 text-xs text-slate-500">
            <span><span className="font-semibold text-navy">Employee ID:</span> {employee.employee_id}</span>
            {employee.joining_date && <span><span className="font-semibold text-navy">Date of Joining:</span> {fmtDate(employee.joining_date)}</span>}
          </div>

          <div className="mt-auto flex w-full items-end justify-between">
            <div className="text-left">
              <p className="text-xs text-slate-500">Issued on</p>
              <p className="text-sm font-semibold text-navy">{issued}</p>
            </div>
            <img src="/vns-stamp.png" alt="stamp" className="h-20 w-20 object-contain opacity-90 -rotate-[8deg]" crossOrigin="anonymous" />
            <div className="text-center">
              <img src="/vns-signature.png" alt="signature" className="mx-auto h-10 object-contain" crossOrigin="anonymous" />
              <div className="mt-1 w-40 border-t border-navy/50 pt-1 text-xs font-semibold text-navy">Founder · VNS Vinayaka</div>
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div className="mt-4 flex justify-center gap-3">
          <Button onClick={download} data-testid={`download-certificate-${employee.employee_id}`} className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Download className="mr-2 h-4 w-4" /> Download</Button>
          <Button onClick={print} variant="outline" data-testid={`print-certificate-${employee.employee_id}`} className="rounded-lg border-navy/20 text-navy hover:bg-navy hover:text-white"><Printer className="mr-2 h-4 w-4" /> Print</Button>
        </div>
      )}
    </div>
  );
};
